/**
 * Julian Day numbers: a continuous count of days used by astronomers.
 *
 * The Julian Day (JD) of an instant is the number of days, with a fraction, since noon
 * Universal Time on 1 January 4713 BCE in the proleptic Julian calendar (31 December 4714 BCE
 * Gregorian). Because the count starts at noon, JD ends in .5 at civil midnight. The Julian
 * Day *Number* (JDN) is the whole number for the civil day that contains a given noon.
 *
 * The Modified Julian Day, MJD = JD − 2 400 000.5, was adopted by the IAU in 1973 so that
 * a day count starts at midnight and needs fewer digits: MJD 0 is 17 November 1858 00:00.
 * Truncated JD (NASA) is JD − 2 440 000.5 and Reduced JD is JD − 2 400 000. Unix time
 * is the same count in seconds from JD 2 440 587.5 (1 January 1970 00:00 UTC).
 *
 * Years are in astronomical numbering — 1 BCE is year 0, 2 BCE is year −1 — as in Jean
 * Meeus, *Astronomical Algorithms* (2nd ed., Willmann-Bell, 1998), chapter 7, whose worked
 * examples the tests reproduce. The date arithmetic reuses the fixed-day functions of
 * Reingold and Dershowitz (`julian.ts` and `rd.ts`): JDN = R.D. + 1 721 425.
 *
 * Nothing here knows about time scales: UT, UTC and TT differ by up to a minute or so
 * today and by hours in antiquity, and the caller must say which one the date is in.
 */

import {
  fixedFromJulian,
  julianFromFixed,
} from "@/lib/tool-kit/calendars/julian";
import { gregorianFromRd, rdFromGregorian } from "@/lib/tool-kit/calendars/rd";
import type { CivilDate } from "@/lib/tool-kit/converters/week-number";

export const JD_OF_RD_ZERO_NOON = 1_721_425;
export const MJD_OFFSET = 2_400_000.5;
export const TJD_OFFSET = 2_440_000.5;
export const RJD_OFFSET = 2_400_000;
export const JD_OF_UNIX_EPOCH = 2_440_587.5;
/** The smallest and largest Julian Day Numbers the tool accepts. */
export const MIN_JDN = 0;
export const MAX_JDN = rdFromGregorian(9999, 12, 31) + JD_OF_RD_ZERO_NOON;

export type CalendarMode = "gregorian" | "julian" | "historical";

/** JDN of 15 October 1582, the first day of the Gregorian calendar. */
export const REFORM_JDN = rdFromGregorian(1582, 10, 15) + JD_OF_RD_ZERO_NOON;

function sameDate(a: CivilDate, b: CivilDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/** Whether a date is the Gregorian one in force (15 October 1582 onwards). */
export function isOnOrAfterReform(date: CivilDate): boolean {
  if (date.year !== 1582) {
    return date.year > 1582;
  }
  return date.month * 100 + date.day >= 1015;
}

/** The JDN of a date in the chosen calendar, or null when the date does not exist. */
export function jdnFromDate(
  mode: CalendarMode,
  date: CivilDate
): number | null {
  const gregorian =
    mode === "gregorian" || (mode === "historical" && isOnOrAfterReform(date));
  const rd = gregorian
    ? rdFromGregorian(date.year, date.month, date.day)
    : fixedFromJulian(date);
  const back = gregorian ? gregorianFromRd(rd) : julianFromFixed(rd);
  if (!sameDate(back, date)) {
    return null;
  }
  // The days dropped at the reform (5–14 October 1582) are Julian dates that never existed
  // in the historical calendar.
  if (mode === "historical" && !gregorian) {
    const reformRd = REFORM_JDN - JD_OF_RD_ZERO_NOON;
    if (rd >= reformRd) {
      return null;
    }
  }
  const jdn = rd + JD_OF_RD_ZERO_NOON;
  return jdn >= MIN_JDN && jdn <= MAX_JDN ? jdn : null;
}

/** The date of a JDN in the chosen calendar. */
export function dateFromJdn(mode: CalendarMode, jdn: number): CivilDate {
  const rd = jdn - JD_OF_RD_ZERO_NOON;
  const gregorian =
    mode === "gregorian" || (mode === "historical" && jdn >= REFORM_JDN);
  return gregorian ? gregorianFromRd(rd) : julianFromFixed(rd);
}

/** 0 = Sunday … 6 = Saturday, for a JDN. */
export function weekdayFromJdn(jdn: number): number {
  return (((jdn + 1) % 7) + 7) % 7;
}

/** Julian Day of a civil date and a time of day in seconds after midnight. */
export function jdFromParts(jdn: number, secondsOfDay: number): number {
  return jdn - 0.5 + secondsOfDay / 86_400;
}

export interface DayParts {
  jdn: number;
  /** Seconds after civil midnight, rounded to the millisecond, in [0, 86 400). */
  secondsOfDay: number;
}

/** Splits a Julian Day into its civil day and the time within it. */
export function partsFromJd(jd: number): DayParts {
  const shifted = jd + 0.5;
  let jdn = Math.floor(shifted);
  let seconds = Math.round((shifted - jdn) * 86_400 * 1000) / 1000;
  if (seconds >= 86_400) {
    jdn += 1;
    seconds -= 86_400;
  }
  return { jdn, secondsOfDay: seconds };
}

const TIME = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:[.,](\d{1,3}))?)?$/;

/** `HH:MM`, `HH:MM:SS` or `HH:MM:SS.mmm` as seconds after midnight, or null. */
export function parseTimeOfDay(text: string): number | null {
  const match = TIME.exec(text.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? 0);
  const millis = Number((match[4] ?? "0").padEnd(3, "0"));
  if (hours > 23 || minutes > 59 || seconds > 59) {
    return null;
  }
  return hours * 3600 + minutes * 60 + seconds + millis / 1000;
}

/** Seconds after midnight as `HH:MM:SS` (with `.mmm` only when there is a fraction). */
export function formatTimeOfDay(secondsOfDay: number): string {
  const whole = Math.floor(secondsOfDay);
  const millis = Math.round((secondsOfDay - whole) * 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  const clock = `${pad(Math.floor(whole / 3600))}:${pad(Math.floor((whole % 3600) / 60))}:${pad(whole % 60)}`;
  return millis === 0 ? clock : `${clock}.${String(millis).padStart(3, "0")}`;
}
